import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Utensils, Heart, Flame, MapPin, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AboutPage = () => {
  const [featuredDishes, setFeaturedDishes] = useState([]);

  useEffect(() => {
    const fetchDishes = async () => {
      try {
        const response = await axios.get(`${API}/menu`);
        // Get dishes with uploaded images
        const withImages = response.data.filter(item => 
          item.image_url && item.image_url.includes('emergentagent.com')
        );
        setFeaturedDishes(withImages.slice(0, 4));
      } catch (error) {
        console.error('Error fetching dishes:', error);
      }
    };
    fetchDishes();
  }, []);

  const values = [
    {
      icon: Flame,
      title: "Authentic Flavours",
      description: "Traditional recipes perfected over generations, from our signature Black Hummus to succulent Aged Ribeye steaks."
    },
    {
      icon: Utensils,
      title: "Artisan Craftsmanship",
      description: "Every dish is hand-crafted with precision—from our Black Garlic Naan baked in a 500°C tandoor to delicate Crispy Ceviche."
    },
    {
      icon: Heart,
      title: "Passion for Perfection",
      description: "Our chefs bring decades of expertise to create dishes like the Chicken Roulade Makhani and Reva Risotto with Lamb Shank."
    },
    {
      icon: Award,
      title: "Premium Ingredients",
      description: "We source the finest British beef, Scottish trout, and spices from Kerala to Kashmir for uncompromising quality."
    }
  ];

  const signatureDishes = [
    {
      name: "Black Hummus",
      description: "Our iconic cold entree featuring activated charcoal, premium Lebanese tahini, and a secret blend of spices.",
      category: "Cold Entrees"
    },
    {
      name: "Aged Ribeye Steak",
      description: "45-day dry-aged British beef, seared to perfection and served with our signature spiced butter.",
      category: "Reva Steaks"
    },
    {
      name: "Black Garlic Naan",
      description: "40-day fermented black garlic folded into our tandoor-baked naan, brushed with clarified butter.",
      category: "Breads"
    },
    {
      name: "Kajar Tart with Saffron Basundi",
      description: "A sweet finale—traditional carrot dessert reimagined with Kashmiri saffron cream.",
      category: "Desserts"
    }
  ];

  return (
    <div className="min-h-screen pt-20 md:pt-24 bg-gradient-to-b from-silver-bright to-white" data-testid="about-page">
      {/* Hero Banner */}
      <section className="page-hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1766232315004-25980447bb19?w=1920&q=80"
            alt="Chef preparing dishes"
            className="w-full h-full object-cover"
          />
          <div className="page-hero-overlay" />
        </div>
        <div className="page-hero-content">
          <div className="max-w-3xl">
            <p className="text-malva-light font-sans text-sm tracking-[0.3em] uppercase mb-4">
              Our Story
            </p>
            <h1 className="font-serif text-4xl md:text-6xl text-white mb-6">
              The Art of Fine Dining
            </h1>
            <p className="text-white/80 text-lg">
              Where centuries-old traditions meet contemporary culinary innovation.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="container-elegant">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-malva-dark font-sans text-sm tracking-[0.3em] uppercase mb-4">
                Our Journey
              </p>
              <h2 className="font-serif text-3xl md:text-5xl text-gray-800 mb-6">
                A Culinary Vision <span className="text-malva-dark">Realized</span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Reva Dining was born from a passion to elevate cuisine to the realm of fine dining. 
                In our kitchen, we honour rich culinary traditions while embracing 
                modern techniques that enhance, never overshadow, authentic flavours.
              </p>
              <p className="text-gray-500 leading-relaxed mb-6">
                Our menu is a celebration of contrasts and harmonies—the earthy depth of our <strong>Black Hummus</strong>, 
                the delicate freshness of <strong>Crispy Ceviche</strong> with Scottish trout, and the indulgent richness 
                of our <strong>45-day Aged Ribeye Steak</strong>. Each dish tells a story of heritage reimagined.
              </p>
              <p className="text-gray-500 leading-relaxed">
                From the fiery tandoor that produces our legendary <strong>Black Garlic Naan</strong> to the 
                slow-braised <strong>Lamb Shank Risotto</strong> that melts on your tongue, every creation 
                at Reva Dining is a testament to our commitment to excellence.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1635355995448-77b02d33621d?w=800&q=80"
                  alt="Spices and ingredients"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-malva/20 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Dishes from Menu */}
      {featuredDishes.length > 0 && (
        <section className="section-padding bg-gray-50">
          <div className="container-elegant">
            <div className="text-center mb-12">
              <p className="text-malva-dark font-sans text-sm tracking-[0.3em] uppercase mb-4">
                From Our Kitchen
              </p>
              <h2 className="font-serif text-3xl md:text-5xl text-gray-800">
                Signature Creations
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {featuredDishes.map((dish, index) => (
                <div key={dish.id} className="group" data-testid={`featured-dish-${index}`}>
                  <div className="aspect-square rounded-xl overflow-hidden shadow-md mb-3">
                    <img
                      src={dish.image_url}
                      alt={dish.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <h4 className="font-serif text-sm md:text-base text-gray-800 text-center">{dish.name}</h4>
                  <p className="text-xs text-malva text-center">{dish.category}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Philosophy - Ingredients */}
      <section className="section-padding bg-white">
        <div className="container-elegant">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1559658166-be35cc5eb9cb?w=800&q=80"
                  alt="Fresh spices and ingredients"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-malva-dark font-sans text-sm tracking-[0.3em] uppercase mb-4">
                Our Ingredients
              </p>
              <h2 className="font-serif text-3xl md:text-5xl text-gray-800 mb-6">
                Sourced with Purpose
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                The soul of our cuisine lies in our ingredients. We source <strong>cardamom from Kerala</strong>, 
                <strong> saffron from Kashmir</strong>, and <strong>chillies from Guntur</strong>. Our steaks 
                come from the finest British cattle, dry-aged for 45 days to develop extraordinary depth.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-malva mt-2 flex-shrink-0" />
                  <p className="text-gray-500"><strong>Scottish Trout</strong> for our Crispy Ceviche, sourced from sustainable Highland farms</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-malva mt-2 flex-shrink-0" />
                  <p className="text-gray-500"><strong>Black Garlic</strong> fermented for 40 days to achieve its signature sweetness</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-malva mt-2 flex-shrink-0" />
                  <p className="text-gray-500"><strong>Premium Tahini</strong> from Lebanon for our signature Black Hummus</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-malva mt-2 flex-shrink-0" />
                  <p className="text-gray-500"><strong>Giant Prawns</strong> grilled over open flames with aromatic spices</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-gray-50">
        <div className="container-elegant">
          <div className="text-center mb-16">
            <p className="text-malva-dark font-sans text-sm tracking-[0.3em] uppercase mb-4">
              What We Stand For
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-gray-800">
              Our Guiding Principles
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={index} 
                className="card-elegant p-8 text-center hover:shadow-xl transition-shadow duration-300"
                data-testid={`value-${index}`}
              >
                <div className="w-16 h-16 rounded-full bg-malva/20 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-malva-dark" />
                </div>
                <h3 className="font-serif text-xl text-gray-800 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Signature Dishes Highlight */}
      <section className="section-padding bg-white">
        <div className="container-elegant">
          <div className="text-center mb-12">
            <p className="text-malva-dark font-sans text-sm tracking-[0.3em] uppercase mb-4">
              Must-Try Dishes
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-gray-800 mb-4">
              Culinary Highlights
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              These dishes embody the essence of Reva Dining—bold flavours, impeccable technique, and unforgettable experiences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {signatureDishes.map((dish, index) => (
              <div 
                key={index} 
                className="flex gap-6 p-6 rounded-xl bg-gradient-to-r from-malva/5 to-transparent border border-malva/10 hover:border-malva/30 transition-colors duration-300"
                data-testid={`signature-dish-${index}`}
              >
                <div className="w-16 h-16 rounded-full bg-malva/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-serif text-malva-dark">{index + 1}</span>
                </div>
                <div>
                  <p className="text-malva text-xs font-sans tracking-wider uppercase mb-1">{dish.category}</p>
                  <h3 className="font-serif text-xl text-gray-800 mb-2">{dish.name}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{dish.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & CTA */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-malva/10">
        <div className="container-elegant">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <MapPin className="w-6 h-6 text-malva-dark" />
              <p className="text-gray-600">45-47 York St, Twickenham, TW1 3LP</p>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-gray-800 mb-6">
              Experience Reva Dining
            </h2>
            <p className="text-gray-500 text-lg mb-8 leading-relaxed">
              Join us for an unforgettable journey through the finest cuisine. 
              From our signature <strong>Black Hummus</strong> to the indulgent <strong>Kajar Tart</strong>, 
              every dish is crafted to create lasting memories.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/booking" 
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="book-table-btn"
              >
                Reserve a Table
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/menu" 
                className="btn-secondary inline-flex items-center justify-center gap-2"
                data-testid="view-menu-btn"
              >
                <Utensils className="w-4 h-4" />
                Explore Our Menu
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
